import React, { useEffect } from "react";
import styles from "./ConfirmOrder.module.scss";
import { CardIcon, PuzzleIcon } from "../../../assets/icons";
import { useDispatch, useSelector } from "react-redux";
import { newBuySolarProductDetailsSelector, newBuySolarProductDetails } from "../../../stores/slices/buy-solar-product-slice";
import { useLazyGetOrderConfirmationDetailsQuery } from "../../../service/order-service";
import { toast } from "react-toastify";
import CommonLoadingSpinner from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { RoofType } from "../../../model/solar-recommendation-form/roofType";
import { isCustomer } from "../../../guards/common/authentification-functions";
import { useNavigate } from "react-router-dom";
import { ApplicationRoutes } from "../../../routes";
import { USER_BILL_TYPE } from "../billing-info/type";

type Props = {};

const ConfirmOrder: React.FC<Props> = (props: Props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const newBuySolarProductInfo = useSelector(newBuySolarProductDetailsSelector);

	const [orderConfirmationDetails, { isLoading, data }] = useLazyGetOrderConfirmationDetailsQuery();

	const rooftopDetails = (roofType: RoofType, numberOfFloors?: number) => {
		let rooftopDetails = null;

		rooftopDetails = roofType.premiseType;

		if (numberOfFloors) {
			rooftopDetails += `, ${numberOfFloors} floors`;
		}

		rooftopDetails += `, ${roofType.name}`;

		return rooftopDetails;
	};


	useEffect(() => {
		orderConfirmationDetails({
			solarProductId: newBuySolarProductInfo?.productDetailsId!,
			durationOfPaymentsInMonths: newBuySolarProductInfo?.durationOfPaymentsInMonths!,
			numberOfFloors: newBuySolarProductInfo?.numberOfFloors!,
			roofTypeId: newBuySolarProductInfo?.roofTypeId!,
			paymentMethod: newBuySolarProductInfo?.paymentMethod!,
		})
			.unwrap()
			.catch(error => {
				toast.error(error.data?.title || "Something went wrong");
			});
	}, [newBuySolarProductInfo]);

	const handleBuyNowClick = () => {
		dispatch(newBuySolarProductDetails({
			...newBuySolarProductInfo,
			amount: data?.orderInformation?.subTotal,
			totalAmount: data?.orderInformation?.total,
			bill_type: isCustomer()? USER_BILL_TYPE.CUSTOMER: USER_BILL_TYPE.GUEST,
		}));
		if (!isCustomer()) {
			navigate(`/${ApplicationRoutes.CHECKOUT}`, { state: { isInstallment: !!newBuySolarProductInfo?.durationOfPaymentsInMonths } });
		}
		if (isCustomer()) {
			navigate(`/${ApplicationRoutes.BILLING_INFO}`);
		}
	};

	return (
		<div className={styles.loadingContainer}>
			<div className={styles.banner}></div>
			<CommonLoadingSpinner loading={isLoading}>
				<div className={styles.container}>
					<div className={styles.orderConfirmationContainer}>
						<h1>Order Confirmation</h1>
						<p>Free Shipping only in Lagos</p>
						<button className={`standardButton ${styles.buyNowButton}`} onClick={handleBuyNowClick}>
							Buy Now
						</button>
					</div>
					<div className={styles.orderDetailsContainer}>
						<img className={styles.productImage} src={data?.solarProduct.images[0].imageUrl} alt="product" />
						<div className={styles.orderDetails}>
							<div className={styles.orderDetailsHeader}>
								<div className={styles.item}>
									<p>{data?.solarProduct.productName}</p>
									<p>{`₦${data?.orderInformation.productPrice.toLocaleString()}`}</p>
								</div>
							</div>
							<div className={styles.separator} />
							<div className={styles.orderDetailContainer}>
								<div className={styles.paymentPlanContainer}>
									<div className={styles.detailHeader}>
										<CardIcon />
										<h3>Payment Plan</h3>
									</div>
									{(() => {
										switch (newBuySolarProductInfo?.paymentMethod) {
											case "One Time Payment":
												return <b>One-Time Payment</b>;
											case "Installments":
												return (
													<div className={styles.monthlyInstallmentContainer}>
														<b>{newBuySolarProductInfo?.durationOfPaymentsInMonths} months Installment</b>
														<p>
															Total Payment <b> — {`₦${data?.orderInformation.total.toLocaleString()}`}</b>
														</p>
														<p>
															Initial deposit <b> — {`₦${data?.orderInformation.deposit.toLocaleString()}`}</b>
														</p>
														<p>
															Monthly Installment <b> — {`₦${data?.orderInformation.monthlyInstallment.toLocaleString()}`}</b>
														</p>
													</div>
												);
											default:
												return null;
										}
									})()}
								</div>
								{data?.orderInformation.roofType && (
									<div className={styles.addOnContainer}>
										<div className={styles.detailHeader}>
											<PuzzleIcon />
											<h3>Add-on</h3>
										</div>
										{data?.orderInformation.roofType && (
											<>
												<b>Installation</b>
												<p>
													{`${rooftopDetails(data?.orderInformation.roofType, data?.orderInformation.numberOfFloors)}`}
													<b> — {`₦${data?.orderInformation.installmentPrice?.toLocaleString()}`}</b>
												</p>
											</>
										)}
										{/* <b>Insurance</b>
									<p>
										Leadsway Insurance <b>— ₦50,000</b>
									</p> */}
									</div>
								)}
							</div>
						</div>
					</div>
					<div className={styles.separator} />
					<div className={styles.orderPriceContainer}>
						<div className={styles.orderPriceDetails}>
							<div className={styles.item}>
								<p>Subtotal</p>
								<b>{`₦${data?.orderInformation.subTotal.toLocaleString()}`}</b>
							</div>
							<div className={styles.item}>
								<p>Shipping</p>
								<b>{data?.orderInformation.shipping ? `₦${data?.orderInformation.shipping.toLocaleString()}` : "FREE"}</b>
							</div>
							<div className={styles.item}>
								<p>Estimated Tax</p>
								<b>{`₦${data?.orderInformation.tax.toLocaleString()}`}</b>
							</div>
							<div className={styles.separator} />
							<div className={`${styles.item} ${styles.bold}`}>
								<p>Total</p>
								<p>{`₦${data?.orderInformation.total.toLocaleString()}`}</p>
							</div>
							<button className={`standardButton ${styles.buyNowButton}`} onClick={handleBuyNowClick}>
								Buy Now
							</button>
						</div>
					</div>
				</div>
			</CommonLoadingSpinner>
		</div>
	);
};

export default ConfirmOrder;
